import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { MoldService } from '../../../service/api/mold-service';

const { MOLDRELEASE_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'moldrelease';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
    fromDate: null,
    toDate: null,
    isReleased: 0, // Default to Released (0) for initial load
  },
  filterVisible: false,
  tileView: false,
  selectedMoldRelease: null,
  editModalVisible: false,
};

export const fetchMoldsByStatus = createAsyncThunk(
  'mold/fetchMoldsByStatus',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await MoldService.getAllMoldByStatus(filters.isReleased);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load mold receiving');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load  mold receiving');
    }
  },
);

export const fetchMoldReceiving = createAsyncThunk(
  'moldRelease/fetchMoldReceiving',
  async (filters, { rejectWithValue }) => {
    try {
      const response = filters?.initialMoldInfoId !== undefined
        ? await MoldService.getAllToolDetailsByMoldInfoID(filters.initialMoldInfoId)
        : await MoldService.getAllMold(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load mold release');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load mold release');
    }
  },
);

export const addMoldRelease = createAsyncThunk(
  'moldRelease/addMoldRelease',
  async (moldReleasePayload, { rejectWithValue }) => {
    try {
      // Return the static mold data
      const response = await MoldService.saveUpdateRelease(moldReleasePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add mold release');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add mold release');
    }
  },
);

export const editMoldRelease = createAsyncThunk(
  'moldRelease/editMoldRelease',
  async (moldReleasePayload, { rejectWithValue }) => {
    try {
      const response = await MoldService.saveUpdateRelease(moldReleasePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update mold release');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update mold release');
    }
  },
);

export const editMoldReleaseData = createAsyncThunk(
  'moldRelease/editMoldReleaseData',
  async ({ moldReceiving, currentStatus }, { rejectWithValue, getState }) => {
    try {
      let response;
      
      // Check if status filter shows "Released" (value = 0)
      if (currentStatus === 0) {
        response = await MoldService.getMoldReleaseByCategory(moldReceiving.toolID, 'Mold-Release');
      } else {
        response = await MoldService.getMoldReleaseByToolId(moldReceiving.toolID);
      }
      
      if (response.status === 200) {
        const result = { 
          moldReceivingData: moldReceiving, 
          moldReleaseData: response.response,
          isReleased: currentStatus === 0 // Pass isReleased flag based on currentStatus
        };
        return result;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to load mold release data',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load mold release data');
    }
  },
);

export const fetchMoldReceivingWithFilters = () => (dispatch, getState) => {
  const filters = getState().moldrelease.filters;
  dispatch(
    fetchMoldReceiving({
      searchText: filters.searchText,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
    }),
  );
};

export const fetchMoldReleaseWithStatus = () => (dispatch, getState) => {
  const filters = getState().moldrelease.filters;
  dispatch(
    fetchMoldsByStatus({
      isReleased: filters?.isReleased,
    }),
  );
};

export const { reducer, actions } = createSlice({
  name: 'mold-release',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setFromDate: (state, action) => {
      state.filters.fromDate = action.payload;
    },
    setToDate: (state, action) => {
      state.filters.toDate = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.isReleased = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMoldReceiving.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMoldReceiving.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMoldReceiving.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMoldsByStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMoldsByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMoldsByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addMoldRelease.fulfilled, (state, action) => {
        const newMoldRelease = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newMoldRelease, ...state.data];
      })
      .addCase(addMoldRelease.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editMoldRelease.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editMoldRelease.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(editMoldReleaseData.pending, state => {
        state.releaseLoading = true;
      })
      .addCase(editMoldReleaseData.fulfilled, state => {
        state.releaseLoading = false;
      })
      .addCase(editMoldReleaseData.rejected, state => {
        state.releaseLoading = false;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addMoldRelease.rejected, editMoldRelease.rejected, editMoldReleaseData.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addMoldRelease.fulfilled, editMoldRelease.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(MOLDRELEASE_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Mold Release saved successfully',
        type: 'success',
      }),
    );
    listenerApi.dispatch(fetchMoldReceivingWithFilters());
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editMoldReleaseData.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(MOLDRELEASE_ADD_EDIT, action.payload));
    listenerApi.dispatch(fetchMoldReceivingWithFilters());
  },
});

redux.injectReducer(STORE_KEY, reducer);
